import React, { useCallback, useEffect, useMemo, useState } from "react";
import ReactFlow, { Background, Controls, Position, useReactFlow } from "reactflow";
import "reactflow/dist/style.css";
import CustomJobNode from "layouts/admin/Organization/components/JobView/components/CustomJobNode"; // your custom node component
import LoadingScreen from "components/Loading";
import { getManpowerDashboardData } from "services/api";
import { useAuth } from "context/authContext";
import { addManpowerRequirement } from "services/api";
import { getManpowerOrgChartData } from "services/api";
import {
  Box,
  Dialog,
  DialogActions,
  DialogTitle,
  IconButton,
  TextField,
  Tooltip,
} from "@mui/material";
import MDButton from "components/MDButton";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Download, FilterAltOff, UnfoldLess, UnfoldMore } from "@mui/icons-material";
import { getManpowerOverviewExport } from "services/api";
import { InsightCard, RenderRegexContent, RenderBulletList } from "services/utils";
import AiUpdateButton from "layouts/admin/StrategicInfo/components/AIIconButton";
import {
  AutoAwesome as AiIcon,
  ThumbUp as StrengthIcon,
  ThumbDown as WeaknessIcon,
  TipsAndUpdates as SuggestionIcon,
} from "@mui/icons-material";
import { manpowerPlanningAI } from "services/api";
import { calculateOrgChartLayout } from "services/utils";

export default function WorkloadAnalysisFlow() {
  const { employee } = useAuth();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { fitView, setCenter, getNodes } = useReactFlow();
  const [visibleBubbles, setVisibleBubbles] = useState({
    strengths: true,
    weaknesses: true,
    suggestions: true,
  });
  const [aiMessage, setAIMessage] = useState();

  const [data, setData] = useState([]);
  const [ftes, setFtes] = useState([]);
  const [totalEmployees, setTotalEmployees] = useState();
  const [loading, setLoading] = useState(true);
  const [visibleJobs, setVisibleJobs] = useState([]);
  const [search, setSearch] = useState("");
  const [selectedJob, setSelectedJob] = useState(null); // ✅ for dialog control
  const [openDialog, setOpenDialog] = useState(false);
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const manpowerOrgChartRes = await getManpowerOrgChartData(); // or false, depending on your use case
        if (manpowerOrgChartRes !== "error") {
          // setManpowerAllCardsData(manpowerOrgChartRes);
          setData(manpowerOrgChartRes.sort((a, b) => a.jobId - b.jobId));

          // setOrgChart(orgChartRes); // make sure you have state: const [orgChart, setOrgChart] = useState([]);
        }
      } catch (err) {
        console.error("Error fetching data:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [refreshTrigger]);

  const getJobsWithParents = useCallback(
    (targetJobs) => {
      if (!data) return [];

      const jobsToShow = new Set(targetJobs);

      // Iterate through targets and add their chain of command
      targetJobs.forEach((job) => {
        let currentJob = job;
        while (currentJob.reportingToSolidId) {
          const parent = data.find((p) => p.jobId === currentJob.reportingToSolidId);

          // If parent exists and isn't already added, add it
          if (parent) {
            if (!jobsToShow.has(parent)) {
              jobsToShow.add(parent);
              currentJob = parent; // Move up
            } else {
              break; // Optimization: Parent chain already processed
            }
          } else {
            break; // Orphan node or root reached
          }
        }
      });

      return Array.from(jobsToShow);
    },
    [data]
  );

  const handleSave = async (newData, jobId) => {
    const res = await addManpowerRequirement({ ...newData, jobId });
    if (res !== "error") {
      setRefreshTrigger((prev) => prev + 1);
      const mappedData = data?.map((d) =>
        d.jobId === jobId
          ? { ...res, jobName: d.jobName, reportingToSolidId: d.reportingToSolidId }
          : d
      );

      // ensure new reference
      const sorted = [...mappedData].sort((a, b) => a.jobId - b.jobId);
      setData(sorted);
      // await fetchRevenueGenerators();
    }
  };

  // 2. EFFECT: Initial Load (Show only the Root Job - The Logged In User's Job)
  useEffect(() => {
    if (data && data.length > 0 && employee?.jobId) {
      // Only reset if visibleJobs is empty (initial load)
      if (visibleJobs.length === 0) {
        const rootJob = data.find((j) => j.jobId === employee.jobId);
        if (rootJob) {
          setVisibleJobs([rootJob]);
        }
      }
    }
  }, [data, employee?.jobId]);

  useEffect(() => {
    // Wait for nodes to render
    const timer = setTimeout(() => {
      if (!nodes.length) return;

      // If searching for 1 specific item, zoom in
      if (search && nodes.length < 5) {
        // Threshold: if few results, zoom in
        const match = nodes.find((n) => n.data.label.toLowerCase().includes(search.toLowerCase()));
        if (match) {
          setCenter(match.position.x + match.width / 2, match.position.y + 50, {
            zoom: 1.2,
            duration: 800,
          });
          return;
        }
      }

      // Otherwise, fit everything in view
      fitView({ padding: 0.2, duration: 800 });
    }, 50);

    return () => clearTimeout(timer);
  }, [visibleJobs, search, fitView, setCenter]);

  const handleExpandAll = () => {
    setVisibleJobs(data);
  };

  const handleCollapseAll = () => {
    // Show only root nodes
    const allIds = new Set(data.map((j) => j.jobId));
    const roots = data.filter((j) => !j.reportingToSolidId || !allIds.has(j.reportingToSolidId));
    setVisibleJobs(roots);
  };

  // 3. HANDLER: Toggle Logic
  const handleJobToggle = (event, jobId, isExpanded) => {
    event?.stopPropagation();

    if (isExpanded) {
      // --- COLLAPSE: Remove all descendants ---
      const getDescendants = (parentId) => {
        const children = data.filter((j) => j.reportingToSolidId === parentId);
        let descendants = [...children];
        children.forEach((child) => {
          descendants = [...descendants, ...getDescendants(child.jobId)];
        });
        return descendants;
      };

      const toRemove = getDescendants(jobId);
      const removeIds = new Set(toRemove.map((j) => j.jobId));

      setVisibleJobs((prev) => prev.filter((j) => !removeIds.has(j.jobId)));
    } else {
      // --- EXPAND: Add immediate children ---
      const children = data.filter((j) => j.reportingToSolidId === jobId);

      if (children.length > 0) {
        setVisibleJobs((prev) => {
          const currentIds = new Set(prev.map((j) => j.jobId));
          const newChildren = children.filter((c) => !currentIds.has(c.jobId));
          return [...prev, ...newChildren];
        });
      }
    }
  };

  useEffect(() => {
    if (aiMessage) {
      setVisibleBubbles({ strengths: true, weaknesses: true, suggestions: true });
    }
  }, [aiMessage]);

  const handleCloseBubble = (key) => {
    setVisibleBubbles((prev) => ({ ...prev, [key]: false }));
  };

  const sendMessage = async () => {
    const res = await manpowerPlanningAI(1, true);
    setAIMessage(res);
  };

  const { nodes, edges, nodeTypes } = useMemo(() => {
    // Safety check
    if (!visibleJobs || visibleJobs.length === 0 || !employee?.jobId) {
      return { nodes: [], edges: [], nodeTypes: {} };
    }

    // A. Call Utility on VISIBLE jobs
    const {
      nodeMap,
      edges: calculatedEdges,
      processedJobs, // This will be the visible jobs sorted for layout
    } = calculateOrgChartLayout(visibleJobs, {
      rootId: employee.jobId,
      isJob: true, // Use the taller spacing we configured earlier
      vSpacing: 150, // Fallback if isJob logic inside utility needs tweaking
      hSpacing: 70,
    });

    // B. Build Nodes
    const nodes = processedJobs.map((job) => {
      // 1. Get Layout Info
      const layoutData = nodeMap.get(job.jobId) ?? { x: 0, y: 0, width: 180 };

      // 2. Determine Toggle State
      // Check if children exist in the FULL dataset
      const hasChildrenInDB = data?.some((j) => j.reportingToSolidId === job.jobId);
      // Check if children are currently VISIBLE
      const isExpanded = visibleJobs.some((j) => j.reportingToSolidId === job.jobId);

      return {
        id: job.jobId.toString(),
        type: "customNode",
        // 3. Define Handle Positions
        sourcePosition: Position.Bottom,
        targetPosition: Position.Top,
        data: {
          label: job.jobName,
          // Toggle Props
          id: job.jobId,
          isExpanded: isExpanded,
          isLeaf: !hasChildrenInDB,
          onToggle: handleJobToggle,

          // Existing Props
          manpowerReq: job.requirementId != null ? job : null,
          manpowerReqDataRecieved: true,
          handleSave: (newData) => handleSave(newData, job.jobId),
          fte: job.fte,
          totalEmployees: job.totalCompanyEmployees,
          job,
          allJobs: data, // Pass full data for dialogs
          isScenario: false,
          loc: "/manpower/team",
        },
        // 4. Position & Style
        position: { x: layoutData.x, y: layoutData.y },
        width: layoutData.width,
        style: {
          cursor: "pointer",
          border: "1px solid #ccc",
          backgroundColor: "#fff",
          width: `${layoutData.width}px`, // Use calculated width
          height: "auto",
          borderRadius: "8px",
        },
      };
    });

    return { nodes, edges: calculatedEdges, nodeTypes: { customNode: CustomJobNode } };
  }, [
    visibleJobs, // Changed from 'data' to 'visibleJobs'
    data, // Still needed for checking hasChildrenInDB
    ftes,
    totalEmployees,
    employee?.jobId,
  ]);

  // ✅ Handle node click
  const handleNodeClick = (event, node) => {
    return;
  };

  const handleConfirm = () => {
    setOpenDialog(false);
    if (selectedJob)
      navigate("/job-details", {
        state: {
          jobId: selectedJob?.jobId,
        },
      });
  };

  useEffect(() => {
    if (!data || data.length === 0) return;

    // --- CASE A: SEARCH IS ACTIVE ---
    if (search) {
      const matches = data.filter((j) => j.jobName.toLowerCase().includes(search.toLowerCase()));
      setVisibleJobs(getJobsWithParents(matches));
    }

    // --- CASE C: DEFAULT (Reset) ---
    // Only reset if we are currently viewing nothing (initial load)
    // OR if the user just cleared the filters manually
    else {
      // Logic: Show only Root Nodes (CEO / Top Level)
      const allIds = new Set(data.map((j) => j.jobId));
      const roots = data.filter((j) => !j.reportingToSolidId || !allIds.has(j.reportingToSolidId));
      setVisibleJobs(roots);
    }
  }, [data, search, getJobsWithParents]);

  if (loading) return <LoadingScreen />;

  return (
    <>
      {/* ✅ Top bar with Export button */}
      <div
        style={{
          display: "flex",
          justifyContent: "flex-end",
          alignItems: "center",
          marginBottom: "10px",
          paddingRight: "20px",
        }}
      >
        <MDButton
          variant="contained"
          color="primary"
          startIcon={<Download size={18} />}
          onClick={async () => {
            const res = await getManpowerOverviewExport();
            if (res === "error") {
              console.error("Export failed");
            }
          }}
          style={{
            marginRight: 5,
          }}
        >
          {t("common.export")}
        </MDButton>
        <AiUpdateButton handleAICall={sendMessage} />
      </div>
      {/* Toolbar: Search, Filter, Actions */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 2,
          mb: 2,
          flexWrap: "wrap", // Allows wrapping on small screens
        }}
      >
        {/* 1. Search Input (Smaller width) */}
        <TextField
          label={t("admin.jobs.searchNodes")}
          variant="outlined"
          size="small" // Makes it more compact
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          sx={{ width: { xs: "100%", md: 220 } }} // Fixed width on desktop
        />

        {/* 3. Expand / Collapse All Toggles */}
        <Box sx={{ display: "flex", gap: 1, borderLeft: "1px solid #ccc", pl: 2 }}>
          <Tooltip title={t("admin.manpowerRequirement.expandAllJobs")}>
            <IconButton onClick={handleExpandAll} color="primary" size="small">
              <UnfoldMore />
            </IconButton>
          </Tooltip>

          <Tooltip title={t("admin.manpowerRequirement.collapseToRoots")}>
            <IconButton onClick={handleCollapseAll} color="primary" size="small">
              <UnfoldLess />
            </IconButton>
          </Tooltip>

          {/* Optional: Reset Filter Button */}
          {search && (
            <Tooltip title="Reset Filters">
              <IconButton
                onClick={() => {
                  setSearch("");
                }}
                color="error"
                size="small"
              >
                <FilterAltOff />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      </Box>

      {/* ✅ Main content */}
      <div
        style={{
          width: "100%",
          height: "80vh",
          position: "relative", // 👈 REQUIRED
          overflow: "hidden",
        }}
      >
        <ReactFlow
          nodes={nodes}
          edges={edges}
          nodeTypes={nodeTypes}
          fitView
          minZoom={0.1}
          onNodeClick={handleNodeClick}
        >
          <Background color="#aaa" gap={16} />
          <Controls />
        </ReactFlow>

        {/* OVERLAY LAYER */}
        {aiMessage && (
          <Box
            sx={{
              position: "absolute", // 👈 overlay
              top: 16,
              right: 16,
              zIndex: 10, // 👈 above ReactFlow
              display: "flex",
              flexDirection: "column",
              gap: 2,
              overflowY: "auto",
            }}
          >
            {/* STRENGTHS */}
            {aiMessage.strengths?.length > 0 && (
              <InsightCard
                title={t("Strengths")}
                icon={<StrengthIcon color="success" fontSize="small" />}
                color="#4CAF50"
                visible={visibleBubbles.strengths}
                onClose={() => handleCloseBubble("strengths")}
              >
                <RenderBulletList items={aiMessage.strengths} type="strength" />
              </InsightCard>
            )}

            {/* WEAKNESSES */}
            {aiMessage.weaknesses?.length > 0 && (
              <InsightCard
                title={t("Weaknesses")}
                icon={<WeaknessIcon color="error" fontSize="small" />}
                color="#F44336"
                visible={visibleBubbles.weaknesses}
                onClose={() => handleCloseBubble("weaknesses")}
              >
                <RenderBulletList items={aiMessage.weaknesses} type="weakness" />
              </InsightCard>
            )}

            {/* SUGGESTIONS */}
            {aiMessage.suggestions && (
              <InsightCard
                title={t("AI Suggestions")}
                icon={<SuggestionIcon color="info" fontSize="small" />}
                color="#2196F3"
                visible={visibleBubbles.suggestions}
                onClose={() => handleCloseBubble("suggestions")}
              >
                <RenderRegexContent text={aiMessage.suggestions} />
              </InsightCard>
            )}
          </Box>
        )}
      </div>

      {/* ✅ Confirmation Dialog */}
      <Dialog open={openDialog} onClose={() => setOpenDialog(false)}>
        <DialogTitle>
          {t("user.manpowerPlanningAnalysis.openJobDescription", {
            jobName: selectedJob?.jobName,
          })}
        </DialogTitle>

        <DialogActions>
          <MDButton onClick={() => setOpenDialog(false)}>{t("common.no")}</MDButton>
          <MDButton onClick={handleConfirm} variant="contained" color="info">
            {t("common.yes")}
          </MDButton>
        </DialogActions>
      </Dialog>
    </>
  );
}
